import { useEffect, useState } from 'react';
import { Modal } from '@components/common';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Experience } from '@components/templates/job/after-apply/Experience';
import { Languages } from '@components/templates/job/after-apply/Languages';
import {
  ApiApplyWizardStepList,
  fetchAbort,
  fetcher,
  isApplyWizardStepListOk,
  JobQuestion,
} from '@utils/data-fetching';
import { ApplyWizardStepList } from 'src/types/apply-wizard';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { Study } from '@components/templates/job/after-apply/Study';
import { useSwrJobMiniInterviewQuestions } from '@root/src/hooks/useSwrApi/endpoints/job/useSwrJobMiniInterviewQuestions';
import { InterviewQuestionsForm } from '../../forms/interview/InterviewQuestions';
import JobAiInterview from './job-interview/JobAiInterview';

interface MissingStepsModalProps {
  jobId: string;
  jobSlug: string;
  jobMiniInterviewQuestions?: JobQuestion[];
  successCallback?: () => void;
  noMissingStepsCallback?: () => void;
}

/**
 * @description Shows a modal for each missing data step, after applying to a job
 * Displays modals for skills, languages and experience that are missing from the user's profile
 *
 * - **jobId** - The job ID used to fetch missing steps (as a string)
 * - **successCallback** - Optional callback function that is called when all steps are completed or when flow is closed early
 * - **noMissingStepsCallback** - Optional callback function that is called when there are no missing steps
 */
export const MissingStepsModal = NiceModal.create(
  (props: MissingStepsModalProps) => {
    const {
      jobId,
      jobSlug,
      jobMiniInterviewQuestions = [],
      successCallback,
      noMissingStepsCallback,
    } = props;
    const modal = useModal();
    const { t } = useTranslation('common');

    // Get locale from router
    const { locale } = useExtendedRouter();

    const [missingSteps, setMissingSteps] = useState<string[]>([]);
    const [stepToShow, setStepToShow] = useState<string>('');
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const { data: miniInterviewQuestions } = useSwrJobMiniInterviewQuestions({
      jobSlug,
      locale,
      shouldFetch: jobMiniInterviewQuestions.length === 0 && missingSteps.includes(ApplyWizardStepList.miniInterview),
    });

    useEffect(() => {
      const { controller, timeoutId } = fetchAbort();

      // get missing data
      void fetcher<ApiApplyWizardStepList>(
        `/v1/apply-wizard/${jobId}/step-list`,
        locale,
        {
          externalAbort: { extController: controller, extTimeout: timeoutId },
        },
      ).then((response) => {
        if (isApplyWizardStepListOk(response)) {
          // Keep the steps that are handled in the rendering
          const filteredSteps = response.filter((step) => [
            ApplyWizardStepList.language,
            ApplyWizardStepList.study,
            ApplyWizardStepList.experience,
            ApplyWizardStepList.miniInterview,
            ApplyWizardStepList.videoInterview,
          ].includes(step as ApplyWizardStepList));
          setMissingSteps(filteredSteps);

          if (filteredSteps.length > 0) {
            setModalOpen(true);
          } else {
            // If no missing steps, call noMissingStepsCallback if provided
            noMissingStepsCallback?.();
          }
        }
      });

      return () => {
        controller.abort();
      };
    }, [jobId, locale, noMissingStepsCallback]);

    // resets data when we have missing steps information available
    useEffect(() => {
      if (missingSteps?.length) {
        setCurrentStep(1);
        setStepToShow(missingSteps[0]);
      }
    }, [missingSteps, modal]);

    const onCloseModal = () => {
      setModalOpen(false);
      successCallback?.();
    };

    const noExpAction = () => {
      if (missingSteps.includes(ApplyWizardStepList.study)) {
        setCurrentStep((prevState) => prevState + 1);
        setStepToShow(missingSteps[currentStep]);
      }
    };

    const onSaveSuccess = () => {
      const oldCurrentStep = currentStep;
      setCurrentStep((prevState) => prevState + 1);
      setStepToShow(missingSteps[oldCurrentStep]);

      // Finished all steps
      if (missingSteps.length > 0 && currentStep === missingSteps.length) {
        // Reset missing steps
        setMissingSteps([]);
        // Call success callback if provided
        successCallback?.();
      }
    };

    return (
      <>
        {missingSteps?.length > 0 && currentStep <= missingSteps?.length && (
          <Modal
            size="lg"
            isOpen={modalOpen}
            onClose={onCloseModal}
            header={
              {
                // title of the modal
                [ApplyWizardStepList.experience]: t('apply.wizard.experience.title'),
                [ApplyWizardStepList.language]: t('apply.wizard.languages.title'),
                [ApplyWizardStepList.study]: t('apply.wizard.study.title'),
                [ApplyWizardStepList.miniInterview]: t('apply.wizard.mini_interview.title'),
                [ApplyWizardStepList.videoInterview]: t('apply.wizard.video_interview.title'),
              }[stepToShow]
            }
            headerClasses="text-xl"
            body={(
              <div className="p-6">
                {
                  {
                    // content of the modal based on step
                    [ApplyWizardStepList.experience]: (
                      <Experience
                        noExpAction={noExpAction}
                        onSaveSuccess={onSaveSuccess}
                        locale={locale}
                      />
                    ),
                    [ApplyWizardStepList.study]: (
                      <Study onSaveSuccess={onSaveSuccess} locale={locale} />
                    ),
                    [ApplyWizardStepList.language]: (
                      <Languages
                        onSaveSuccess={onSaveSuccess}
                        locale={locale}
                      />
                    ),
                    [ApplyWizardStepList.miniInterview]: (
                      <InterviewQuestionsForm
                        jobQuestion={
                          jobMiniInterviewQuestions?.length > 0
                            ? jobMiniInterviewQuestions
                            : (miniInterviewQuestions ?? []).map(({ id, text }) => ({
                              id: id?.toString(),
                              question: text,
                            } as JobQuestion))
                        }
                        jobSlug={jobSlug}
                        locale={locale}
                        onSaveSuccess={onSaveSuccess}
                      />
                    ),
                    [ApplyWizardStepList.videoInterview]: (
                      <JobAiInterview jobSlug={jobSlug} locale={locale} />
                    ),
                  }[stepToShow]
                }
              </div>
            )}
          />
        )}
      </>
    );
  },
);

/**
 * @description Opens the missing steps modal anywhere in the app
 * This modal guides users to complete their profile by filling in missing information
 * after they have applied to a job.
 *
 * - **jobId** - The job ID used to fetch missing steps (as a string)
 * - **successCallback** - Optional callback function that is called when all steps are completed
 * - **noMissingStepsCallback** - Optional callback function that is called when there are no missing steps
 *
 * @example
 * // import the function
 * import { showMissingStepsModal } from '@components/templates/job/after-apply/MissingStepsModal';
 * // usage
 * <Button onClick={() => showMissingStepsModal({ jobId: String(job.id), successCallback })}>Show missing steps modal</Button>
 */
export const showMissingStepsModal = (props: MissingStepsModalProps) => {
  void NiceModal.show(MissingStepsModal, props);
};
